import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, Plug, Trash, Plus, ShieldCheck, ChartBar, MagnifyingGlass, Brain, ArrowsLeftRight } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const PROVIDERS = [
    {
        key: "apollo",
        label: "Apollo.io",
        description: "B2B company database. Reserved for advanced AI research (coming soon).",
        keyLabel: "Apollo API Key",
        extra: [],
    },
    {
        key: "apify",
        label: "Apify (Google Maps)",
        description: "Scrape Google Maps results for real local businesses by query + location. Used as a Discover source.",
        keyLabel: "Apify API Token",
        extra: [{ key: "actor", label: "Actor ID (optional)", placeholder: "compass~crawler-google-places" }],
    },
];

export default function Settings() {
    const { user } = useAuth();
    const [tab, setTab] = useState("users");
    const isAdmin = user?.role === "admin" || user?.role === "owner";

    if (!isAdmin) {
        return (
            <div className="card-os p-10 text-center" data-testid="settings-no-access">
                <ShieldCheck size={32} className="mx-auto text-muted-foreground" />
                <h2 className="font-heading text-2xl font-bold mt-3">Admins only</h2>
                <p className="text-muted-foreground text-sm mt-1">
                    Settings, users, and integrations are restricted to admin accounts.
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="settings-page">
            <div>
                <div className="overline text-muted-foreground">Workspace</div>
                <h1 className="font-heading text-4xl sm:text-5xl font-black mt-2 leading-none">Settings</h1>
            </div>

            <div className="inline-flex border border-border rounded-sm overflow-hidden">
                {["activity", "users", "integrations"].map((t, i) => (
                    <button key={t} data-testid={`tab-${t}`}
                        onClick={() => setTab(t)}
                        className={`inline-flex items-center gap-2 px-4 py-2 text-sm capitalize transition-colors ${
                            tab === t ? "bg-foreground text-white" : "bg-white text-foreground hover:bg-muted"
                        } ${i > 0 ? "border-l border-border" : ""}`}>
                        {t === "users" ? <Users size={14} /> : t === "integrations" ? <Plug size={14} /> : <ChartBar size={14} />} {t}
                    </button>
                ))}
            </div>

            {tab === "activity" ? <ActivityTab /> : tab === "users" ? <UsersTab currentUser={user} /> : <IntegrationsTab />}
        </div>
    );
}

function ActivityTab() {
    const [stats, setStats] = useState(null);
    const [logs, setLogs] = useState([]);
    const [days, setDays] = useState(7);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        Promise.all([
            api.get(`/audit/stats?days=${days}`).then((r) => setStats(r.data)).catch(() => {}),
            api.get("/audit/logs?limit=150").then((r) => setLogs(r.data)).catch(() => {}),
        ]).finally(() => setLoading(false));
    }, [days]);

    const ACTION_META = {
        leads_scraped: { label: "Scraped leads", icon: <MagnifyingGlass size={14} className="text-brand" /> },
        lead_researched: { label: "Researched", icon: <Brain size={14} className="text-signal-ai" /> },
        stage_changed: { label: "Status change", icon: <ArrowsLeftRight size={14} className="text-foreground" /> },
    };

    const fmt = (iso) => new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

    const describe = (lg) => {
        const d = lg.details || {};
        if (lg.action === "leads_scraped") return `${d.count ?? 0} leads · "${d.query || ""}"${d.location ? ` in ${d.location}` : ""}`;
        if (lg.action === "lead_researched") return d.company_name || "—";
        if (lg.action === "stage_changed") return `${d.company_name || "Lead"}: ${(d.from || "").replace(/_/g, " ")} → ${(d.to || "").replace(/_/g, " ")}`;
        return "";
    };

    return (
        <div className="space-y-6" data-testid="activity-tab">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="overline text-muted-foreground">Team audit trail</div>
                <select data-testid="audit-range" value={days} onChange={(e) => setDays(Number(e.target.value))} className="input-os py-1 text-sm w-auto">
                    <option value={7}>Last 7 days</option>
                    <option value={14}>Last 14 days</option>
                    <option value={30}>Last 30 days</option>
                </select>
            </div>

            {/* Totals */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="audit-totals">
                <AuditStat label="Leads scraped" value={stats?.totals?.leads_scraped ?? 0} icon={<MagnifyingGlass size={18} className="text-brand" />} />
                <AuditStat label="Leads researched" value={stats?.totals?.leads_researched ?? 0} icon={<Brain size={18} className="text-signal-ai" />} />
                <AuditStat label="Status changes" value={stats?.totals?.status_changes ?? 0} icon={<ArrowsLeftRight size={18} className="text-foreground" />} />
            </div>

            {/* Daily breakdown */}
            <div className="card-os p-5">
                <h3 className="font-heading text-lg font-bold mb-3">Daily activity</h3>
                {loading ? <p className="text-sm text-muted-foreground">Loading…</p> : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left border-b border-border">
                                    <th className="py-2 pr-4 overline text-muted-foreground font-normal">Date</th>
                                    <th className="py-2 pr-4 overline text-muted-foreground font-normal">Scraped</th>
                                    <th className="py-2 pr-4 overline text-muted-foreground font-normal">Researched</th>
                                    <th className="py-2 overline text-muted-foreground font-normal">Status changes</th>
                                </tr>
                            </thead>
                            <tbody data-testid="audit-daily-rows">
                                {(stats?.days || []).length === 0 ? (
                                    <tr><td colSpan={4} className="py-4 text-muted-foreground">No activity in this period.</td></tr>
                                ) : stats.days.map((d) => (
                                    <tr key={d.date} className="border-b border-border/60">
                                        <td className="py-2 pr-4 font-medium">{d.date}</td>
                                        <td className="py-2 pr-4">{d.leads_scraped}</td>
                                        <td className="py-2 pr-4">{d.leads_researched}</td>
                                        <td className="py-2">{d.status_changes}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Per-user */}
            <div className="card-os p-5">
                <h3 className="font-heading text-lg font-bold mb-3">By team member</h3>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left border-b border-border">
                                <th className="py-2 pr-4 overline text-muted-foreground font-normal">User</th>
                                <th className="py-2 pr-4 overline text-muted-foreground font-normal">Scraped</th>
                                <th className="py-2 pr-4 overline text-muted-foreground font-normal">Researched</th>
                                <th className="py-2 overline text-muted-foreground font-normal">Status changes</th>
                            </tr>
                        </thead>
                        <tbody data-testid="audit-user-rows">
                            {(stats?.by_user || []).length === 0 ? (
                                <tr><td colSpan={4} className="py-4 text-muted-foreground">No activity yet.</td></tr>
                            ) : stats.by_user.map((u) => (
                                <tr key={u.user_name} className="border-b border-border/60">
                                    <td className="py-2 pr-4 font-medium">{u.user_name}</td>
                                    <td className="py-2 pr-4">{u.leads_scraped}</td>
                                    <td className="py-2 pr-4">{u.leads_researched}</td>
                                    <td className="py-2">{u.status_changes}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Recent log */}
            <div className="card-os p-5">
                <h3 className="font-heading text-lg font-bold mb-3">Recent activity</h3>
                <div className="space-y-2" data-testid="audit-log-list">
                    {logs.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No events recorded yet.</p>
                    ) : logs.map((lg) => (
                        <div key={lg.id} className="flex items-start gap-3 py-2 border-b border-border/60 last:border-0">
                            <div className="mt-0.5">{ACTION_META[lg.action]?.icon}</div>
                            <div className="flex-1 min-w-0">
                                <div className="text-sm">
                                    <span className="font-medium">{lg.user_name}</span>
                                    <span className="text-muted-foreground"> · {ACTION_META[lg.action]?.label || lg.action}</span>
                                </div>
                                <div className="text-xs text-muted-foreground truncate">{describe(lg)}</div>
                            </div>
                            <div className="text-xs text-muted-foreground whitespace-nowrap">{fmt(lg.created_at)}</div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function AuditStat({ label, value, icon }) {
    return (
        <div className="card-os p-5">
            <div className="flex items-center justify-between">
                <span className="overline text-muted-foreground">{label}</span>
                {icon}
            </div>
            <div className="font-heading text-4xl font-black mt-2">{value}</div>
        </div>
    );
}

function UsersTab({ currentUser }) {
    const [users, setUsers] = useState([]);
    const [form, setForm] = useState({ email: "", name: "", password: "", role: "executive" });
    const [adding, setAdding] = useState(false);

    const load = () => api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    useEffect(() => { load(); }, []);

    const submit = async (e) => {
        e.preventDefault();
        setAdding(true);
        try {
            await api.post("/users", form);
            toast.success("User added");
            setForm({ email: "", name: "", password: "", role: "executive" });
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not add user");
        } finally { setAdding(false); }
    };

    const changeRole = async (id, role) => {
        try { await api.patch(`/users/${id}`, { role }); toast.success("Role updated"); load(); }
        catch { toast.error("Update failed"); }
    };

    const removeUser = async (id) => {
        if (!window.confirm("Remove this user?")) return;
        try { await api.delete(`/users/${id}`); toast.success("Removed"); load(); }
        catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
    };

    return (
        <div className="space-y-6">
            <div className="card-os p-6">
                <h3 className="font-heading text-xl font-bold">Add a user</h3>
                <p className="text-sm text-muted-foreground mt-1">
                    Admins can manage users and integrations. Executives have access to everything except settings.
                </p>
                <form onSubmit={submit} className="grid md:grid-cols-5 gap-3 mt-4">
                    <input data-testid="new-user-name" required value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className="input-os md:col-span-1" placeholder="Name" />
                    <input data-testid="new-user-email" type="email" required value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        className="input-os md:col-span-1" placeholder="email@company.com" />
                    <input data-testid="new-user-password" type="password" required minLength={6} value={form.password}
                        onChange={(e) => setForm({ ...form, password: e.target.value })}
                        className="input-os md:col-span-1" placeholder="Password (min 6)" />
                    <select data-testid="new-user-role" value={form.role}
                        onChange={(e) => setForm({ ...form, role: e.target.value })}
                        className="input-os md:col-span-1">
                        <option value="executive">Executive</option>
                        <option value="admin">Admin</option>
                    </select>
                    <button type="submit" disabled={adding} data-testid="add-user-btn"
                        className="btn-primary md:col-span-1 inline-flex items-center justify-center gap-1 disabled:opacity-60">
                        <Plus size={14} /> {adding ? "Adding…" : "Add"}
                    </button>
                </form>
            </div>

            <div className="card-os">
                <div className="px-6 py-4 border-b border-border">
                    <h3 className="font-heading text-xl font-bold">Team</h3>
                </div>
                <div className="divide-y divide-border" data-testid="users-list">
                    {users.map((u) => (
                        <div key={u.id} className="flex items-center justify-between px-6 py-3" data-testid={`user-row-${u.id}`}>
                            <div className="min-w-0">
                                <div className="font-medium truncate">{u.name}{u.id === currentUser?.id && (
                                    <span className="text-xs text-muted-foreground"> (you)</span>
                                )}</div>
                                <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                            </div>
                            <div className="flex items-center gap-2">
                                <select value={u.role === "owner" ? "admin" : u.role}
                                    disabled={u.id === currentUser?.id}
                                    onChange={(e) => changeRole(u.id, e.target.value)}
                                    className="input-os py-1 text-sm">
                                    <option value="executive">Executive</option>
                                    <option value="admin">Admin</option>
                                </select>
                                <button onClick={() => removeUser(u.id)}
                                    disabled={u.id === currentUser?.id}
                                    data-testid={`delete-user-${u.id}`}
                                    className="p-1.5 text-muted-foreground hover:text-signal-danger disabled:opacity-30">
                                    <Trash size={14} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function IntegrationsTab() {
    const [integrations, setIntegrations] = useState([]);
    const load = () => api.get("/integrations").then((r) => setIntegrations(r.data)).catch(() => {});
    useEffect(() => { load(); }, []);
    const byProvider = Object.fromEntries(integrations.map((i) => [i.provider, i]));

    return (
        <div className="space-y-4" data-testid="integrations-list">
            {PROVIDERS.map((p) => (
                <IntegrationCard key={p.key} provider={p} current={byProvider[p.key]} onSaved={load} />
            ))}
        </div>
    );
}

function IntegrationCard({ provider, current, onSaved }) {
    const [apiKey, setApiKey] = useState("");
    const [extra, setExtra] = useState({});
    const [saving, setSaving] = useState(false);
    const connected = !!current;

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.put(`/integrations/${provider.key}`, {
                provider: provider.key, api_key: apiKey, extra, enabled: true,
            });
            toast.success(`${provider.label} connected`);
            setApiKey("");
            onSaved();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally { setSaving(false); }
    };

    const disconnect = async () => {
        if (!window.confirm(`Disconnect ${provider.label}?`)) return;
        try { await api.delete(`/integrations/${provider.key}`); toast.success("Disconnected"); onSaved(); }
        catch { toast.error("Failed"); }
    };

    return (
        <div className="card-os p-6" data-testid={`integration-${provider.key}`}>
            <div className="flex items-start justify-between gap-4">
                <div>
                    <div className="flex items-center gap-2">
                        <h3 className="font-heading text-xl font-bold">{provider.label}</h3>
                        {connected && (
                            <span className="chip border-signal-success text-signal-success bg-white">
                                Connected · {current.api_key_preview}
                            </span>
                        )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{provider.description}</p>
                </div>
                {connected && (
                    <button onClick={disconnect} data-testid={`disconnect-${provider.key}`}
                        className="btn-secondary text-xs inline-flex items-center gap-1">
                        <Trash size={12} /> Disconnect
                    </button>
                )}
            </div>
            <form onSubmit={save} className="mt-4 grid md:grid-cols-3 gap-3">
                <div className={provider.extra.length ? "md:col-span-1" : "md:col-span-2"}>
                    <label className="overline text-muted-foreground">{provider.keyLabel}</label>
                    <input data-testid={`apikey-${provider.key}`} type="password" required minLength={4}
                        value={apiKey} onChange={(e) => setApiKey(e.target.value)}
                        className="input-os mt-1" placeholder={connected ? "Enter new key to replace…" : "Paste key…"} />
                </div>
                {provider.extra.map((f) => (
                    <div key={f.key} className="md:col-span-1">
                        <label className="overline text-muted-foreground">{f.label}</label>
                        <input value={extra[f.key] || ""} onChange={(e) => setExtra({ ...extra, [f.key]: e.target.value })}
                            className="input-os mt-1 font-mono text-xs" placeholder={f.placeholder} />
                    </div>
                ))}
                <div className="md:col-span-1 flex items-end">
                    <button type="submit" disabled={saving} data-testid={`save-${provider.key}`}
                        className="btn-primary w-full disabled:opacity-60">
                        {saving ? "Saving…" : connected ? "Update" : "Connect"}
                    </button>
                </div>
            </form>
        </div>
    );
}
